import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { 
  Shield, Zap, Users, DollarSign, BrainCircuit, Activity, 
 Ticket, Radio, Clock, Monitor
} from 'lucide-react';

import ThreatMapWidget from './ThreatMapWidget';

// --- Helper for Uptime ---
const formatUptime = (bootTime) => {
    if (!bootTime) return "Calculating...";
    const now = Date.now() / 1000;
    const diff = now - bootTime;
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${hours}h ${minutes}m`;
};

// --- Components ---
const CircularGauge = ({ value, label, subtext, colorHex }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  // Defensive check for NaN
  const safeValue = isNaN(value) ? 0 : value;
  const strokeDashoffset = circumference - (safeValue / 100) * circumference;

  return (
    <div className="flex flex-col items-center justify-center p-5 bg-white rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden">
       <div className={`absolute top-0 right-0 w-16 h-16 bg-${colorHex} opacity-5 rounded-full blur-xl -mr-8 -mt-8`}></div>
      <div className="relative w-28 h-28">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="56" cy="56" r={radius} stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
          <circle
            cx="56" cy="56" r={radius}
            stroke={colorHex} strokeWidth="8" fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-800">
          <span className="text-xl font-bold tracking-tighter">{safeValue}%</span>
          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{label}</span>
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-500 font-medium bg-slate-50 px-2 py-1 rounded-md">{subtext}</p>
    </div>
  );
};

const KpiCard = ({ title, value, subValue, icon: Icon, color, bg }) => (
  <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between hover:shadow-md transition-all duration-200 group">
    <div>
      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-2xl font-black text-slate-800 group-hover:scale-105 transition-transform origin-left">
        {value}
      </h3>
      {subValue && (
        <p className="text-sm font-bold text-slate-500 mt-1">{subValue}</p>
      )}
    </div>
    <div className={`p-3 rounded-xl ${bg} group-hover:rotate-12 transition-transform`}>
      <Icon className={`w-6 h-6 ${color}`} />
    </div>
  </div>
);

const ActivityItem = ({ type, title, message, time }) => {
  const timeString = new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex items-start space-x-3 pb-4 border-b border-slate-50 last:border-0 last:pb-0">
      <div className={`mt-1.5 w-2 h-2 rounded-full ${type === 'error' ? 'bg-red-500' : 'bg-blue-500'}`}></div>
      <div className="flex-1">
        <div className="flex justify-between items-start">
          <p className="text-sm font-semibold text-slate-700">{title}</p>
          <span className="text-[10px] text-slate-400 flex items-center">
            <Clock className="w-3 h-3 mr-1" /> {timeString}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{message}</p>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => axiosClient.get('/admin/stats'),
    refetchInterval: 3000, 
  });

  if (isLoading || !data) return (
    <div className="flex h-96 items-center justify-center text-slate-400">
      <div className="animate-spin mr-3"><Activity /></div> Synchronizing AIRS Core...
    </div>
  );

  const system = data.system || { cpu_percent: 0, ram_percent: 0, ram_used_gb: 0, ram_total_gb: 0, boot_time: 0 };
  const kpi = data.kpi || {};
  const graphs = data.graphs || { revenue_history: [], threats_history: [], neural_growth: [] };
  const activity_log = data.activity_log || [];
  const device_stats = data.device_stats || { Linux: 0, Windows: 0, MacOS: 0, Android: 0, iOS: 0 };
  
  const revenueUSD = kpi.revenue_usd || 0;
  const revenueINR = kpi.revenue_inr || 0;
  const totalRevenue = kpi.total_revenue || 0; // Fallback for old backend
  
  // Logic: If backend is old (no split), show total as USD. If new, show split.
  const displayRevenueMain = revenueUSD > 0 || revenueINR > 0 ? `$${revenueUSD.toLocaleString()}` : `$${totalRevenue.toLocaleString()}`;
  const displayRevenueSub = revenueUSD > 0 || revenueINR > 0 ? `₹${revenueINR.toLocaleString()}` : null;

  const ticketData = [
    { name: 'Open', value: kpi.tickets_open || 0, color: '#f59e0b' },
    { name: 'Closed', value: kpi.tickets_closed || 0, color: '#10b981' },
  ];

  const deviceChartData = [
    { name: 'Linux', value: device_stats.Linux, color: '#f97316' }, 
    { name: 'Windows', value: device_stats.Windows, color: '#3b82f6' }, 
    { name: 'MacOS', value: device_stats.MacOS, color: '#64748b' }, 
    { name: 'Android', value: device_stats.Android, color: '#22c55e' }, 
    { name: 'iOS', value: device_stats.iOS, color: '#6366f1' }, 
  ].filter(d => d.value > 0); 

  return (
    <div className="space-y-6 animate-fade-in pb-10 text-white">
      
      {/* 1. Header & System */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="md:col-span-2 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white flex flex-col justify-between relative overflow-hidden shadow-lg">
           <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 opacity-10 rounded-full blur-3xl -mr-16 -mt-16"></div>
           <div>
             <div className="flex items-center space-x-2 mb-2">
                <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></div>
                <span className="text-xs font-bold text-emerald-400 tracking-widest uppercase">System Online</span>
             </div>
             <h1 className="text-2xl font-bold">AIRS Command Center</h1>
             <p className="text-slate-400 text-sm mt-1">Global security grid active.</p>
           </div>
           <div className="mt-4 flex space-x-6 text-sm">
              <div><span className="block text-xs text-slate-500">Traffic (24h)</span><span className="font-mono font-bold text-lg">{kpi.traffic_24h || 0}</span></div>
              <div><span className="block text-xs text-slate-500">Server Uptime</span><span className="font-mono font-bold text-lg">{formatUptime(system.boot_time)}</span></div>
           </div>
        </div>
        <CircularGauge value={system.cpu_percent} label="CPU Core" subtext="Load Average" colorHex="#3b82f6" />
        <CircularGauge value={system.ram_percent} label="Memory" subtext={`${system.ram_used_gb} GB Used`} colorHex="#8b5cf6" />
      </div>

      {/* 2. KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard 
            title="Total Revenue" 
            value={displayRevenueMain} 
            subValue={displayRevenueSub}
            icon={DollarSign} 
            color="text-emerald-600" 
            bg="bg-emerald-50" 
        />
        <KpiCard title="Active Users" value={kpi.total_users || 0} icon={Users} color="text-blue-600" bg="bg-blue-50" />
        <KpiCard title="Total Orders" value={kpi.total_orders || 0} icon={Zap} color="text-amber-600" bg="bg-amber-50" />
        <KpiCard title="Protected Agents" value={kpi.active_licenses || 0} icon={Shield} color="text-indigo-600" bg="bg-indigo-50" />
      </div>

      <div className="mb-6">
          <ThreatMapWidget />
      </div>
      {/* 3. Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Threat Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-slate-800 flex items-center"><Shield className="w-5 h-5 mr-2 text-red-500" />Threat Neutralization</h3>
            <div className="text-right">
              <span className="text-2xl font-bold text-slate-800">{kpi.threats_neutralized || 0}</span>
              <span className="block text-xs text-green-500 font-medium">Total Blocked</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={graphs.threats_history}>
                <defs>
                  <linearGradient id="colorThreats" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" hide />
                <YAxis hide />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                <Area type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorThreats)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Device Fleet */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
           <h3 className="font-bold text-slate-800 flex items-center mb-4"><Monitor className="w-5 h-5 mr-2 text-blue-500" />Fleet Status</h3>
           <div className="h-48">
             <ResponsiveContainer width="100%" height="100%">
               <PieChart>
                 <Pie data={deviceChartData} innerRadius={50} outerRadius={70} paddingAngle={5} dataKey="value">
                   {deviceChartData.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={entry.color} />
                   ))}
                 </Pie>
                 <Tooltip />
                 <Legend verticalAlign="bottom" height={36} iconType="circle" />
               </PieChart>
             </ResponsiveContainer>
           </div>
           {deviceChartData.length === 0 && <div className="text-center text-xs text-slate-400 mt-2">No active devices</div>}
        </div>
      </div>

      {/* 4. Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <h3 className="font-bold text-slate-800 flex items-center mb-4"><Ticket className="w-5 h-5 mr-2 text-purple-500" />Support Status</h3>
          <div className="h-32 relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={ticketData} innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value">
                  {ticketData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
               <span className="text-xl font-bold text-slate-700">{(kpi.tickets_open || 0) + (kpi.tickets_closed || 0)}</span>
               <span className="text-[10px] text-slate-400">TICKETS</span>
            </div>
          </div>
          <div className="flex justify-center space-x-4 mt-2">
             <div className="flex items-center text-xs text-slate-600"><div className="w-2 h-2 rounded-full bg-amber-500 mr-2"></div> Open ({kpi.tickets_open || 0})</div>
             <div className="flex items-center text-xs text-slate-600"><div className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></div> Closed ({kpi.tickets_closed || 0})</div>
          </div>
        </div>

        <div className="bg-slate-900 p-6 rounded-2xl shadow-lg text-white relative overflow-hidden">
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-pink-500 opacity-20 rounded-full blur-3xl"></div>
          <div className="flex justify-between items-start mb-6 z-10 relative">
            <div>
              <h3 className="font-bold flex items-center"><BrainCircuit className="w-5 h-5 mr-2 text-pink-500" />Hive Mind</h3>
              <p className="text-xs text-slate-400 mt-1">Learning Patterns</p>
            </div>
            <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500">{kpi.neural_patterns || 0}</p>
          </div>
          <div className="h-32 z-10 relative">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={graphs.neural_growth}>
                <Bar dataKey="value" fill="#ec4899" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col">
           <div className="flex justify-between items-center mb-4">
             <h3 className="font-bold text-slate-800 flex items-center"><Radio className="w-5 h-5 mr-2 text-blue-500" />Live Activity</h3>
             <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded">REAL-TIME</span>
           </div>
           <div className="flex-1 overflow-hidden relative">
              <div className="space-y-2 h-40 overflow-y-auto pr-2 scrollbar-hide">
                 {activity_log.length > 0 ? (
                    activity_log.map((log, i) => <ActivityItem key={i} {...log} time={log.created_at} />)
                 ) : (
                    <div className="text-center text-slate-400 text-sm mt-10">No recent activity</div>
                 )}
              </div>
           </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardPage;